import React, { useState, useEffect } from 'react';
import { Menu, X, CheckCircle2, Folder, Brain, FileText, Zap, AlertCircle, MessageCircle, PanelLeft, Search, Users, Clock, TrendingUp, Book, Download } from 'lucide-react';
import { DocsSidebar } from '../components/docs/DocsSidebar';
import { SearchBar } from '../components/docs/SearchBar';
import { TableOfContents } from '../components/docs/TableOfContents';
import { MainSidebar } from '../components/layout/MainSidebar';
import { Logo } from '../components/common/Logo';

interface DocsPageProps {
  userEmail?: string | null;
  isAnonymous?: boolean;
  onSignIn?: () => void;
  onSignOut?: () => void;
}

export const DocsPage: React.FC<DocsPageProps> = ({
  userEmail = null,
  isAnonymous = false,
  onSignIn = () => {},
  onSignOut = () => {},
}) => {
  const [isDocsSidebarOpen, setIsDocsSidebarOpen] = useState(false);
  const [isMainSidebarOpen, setIsMainSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('welcome');

  const tocItems = [
    { id: 'welcome', title: 'Welcome to CommsGenie', level: 2 },
    { id: 'quick-start', title: 'Quick Start Guide', level: 2 },
    { id: 'understanding-dashboard', title: 'Understanding Your Dashboard', level: 2 },
    { id: 'proceedings', title: 'Proceedings', level: 2 },
    { id: 'chat-modes', title: 'Chat Modes', level: 2 },
    { id: 'search-discovery', title: 'Document Search & Discovery', level: 2 },
    { id: 'comment-summary', title: 'Comment Summary Generation', level: 2 },
    { id: 'export', title: 'Export & Integration', level: 2 },
    { id: 'usage-tiers', title: 'Understanding Usage Tiers', level: 2 },
    { id: 'managing-subscription', title: 'Managing Your Subscription', level: 2 },
    { id: 'effective-questions', title: 'Writing Effective Questions', level: 2 },
    { id: 'research-workflows', title: 'Regulatory Research Workflows', level: 2 },
    { id: 'tips-tricks', title: 'Tips & Tricks', level: 2 },
    { id: 'faq', title: 'Frequently Asked Questions', level: 2 },
    { id: 'getting-help', title: 'Getting Help', level: 2 },
  ];

  // Track scroll position to update active section with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Debounce scroll handler by 100ms
      timeoutId = setTimeout(() => {
        const sections = tocItems.map(item => document.getElementById(item.id));
        // Account for header height (64px) + some padding
        const scrollPosition = window.scrollY + 100;

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = sections[i];
          if (section && section.offsetTop <= scrollPosition) {
            setActiveSection(tocItems[i].id);
            break;
          }
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [tocItems]);

  return (
    <div className="flex flex-col min-h-screen w-full bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 w-full">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsMainSidebarOpen(!isMainSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
              title="Toggle main menu"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex items-center space-x-3">
              <Logo variant="icon-only" size="sm" />
              <h1 className="text-xl font-bold text-gray-900">Documentation</h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsDocsSidebarOpen(!isDocsSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              title="Toggle docs navigation"
            >
              <PanelLeft className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Main App Sidebar - Overlay */}
      <MainSidebar
        isOpen={isMainSidebarOpen}
        onClose={() => setIsMainSidebarOpen(false)}
        alwaysOverlay={true}
        userEmail={userEmail}
        isAnonymous={isAnonymous}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
      />

      {/* Main content */}
      <div className="flex flex-1 w-full min-h-0">
        {/* Docs Navigation Sidebar */}
        <DocsSidebar
          activeSection={activeSection}
          onSectionClick={setActiveSection}
          isMobileOpen={isDocsSidebarOpen}
          onMobileClose={() => setIsDocsSidebarOpen(false)}
        />

        {/* Main content area */}
        <div className="flex-1 min-w-0 py-8 px-6">
            {/* Search bar */}
            <SearchBar className="mb-8" />

            {/* Documentation content */}
            <div className="bg-white rounded-lg border border-gray-200 p-8 lg:p-12">
              <article className="prose prose-lg max-w-none">
                {/* Getting Started Section */}
                <section id="welcome" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to CommsGenie</h2>
                  <p className="text-gray-700 text-lg mb-4">
                    CommsGenie is your AI-powered assistant for navigating FCC regulatory documents. 
                    Whether you're analyzing complex dockets, tracking regulatory changes, or conducting 
                    deep research across multiple documents, CommsGenie helps you work faster and smarter.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center">
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      What You Can Do with CommsGenie
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        <span>Chat with FCC dockets and get instant answers to your questions</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        <span>Analyze multiple documents simultaneously with cross-document collections</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        <span>Track changes over time with temporal analysis</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        <span>Generate summaries and extract key information from complex filings</span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Quick Start Guide */}
                <section id="quick-start" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Quick Start Guide</h2>
                  
                  <div className="space-y-6">
                    <div className="border-l-4 border-green-500 pl-6 py-2">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Step 1: Create Your Account</h3>
                      <p className="text-gray-700">
                        Sign up and choose your plan. Free accounts include 5 AI messages to explore the platform. 
                        Paid plans unlock unlimited messaging and advanced features.
                      </p>
                    </div>

                    <div className="border-l-4 border-blue-500 pl-6 py-2">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Step 2: Navigate to Your Workspace</h3>
                      <p className="text-gray-700">
                        Access your personal workspace or create a team workspace for collaboration. Workspaces let you
                        organize and share research with colleagues.
                      </p>
                    </div>

                    <div className="border-l-4 border-purple-500 pl-6 py-2">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Step 3: Select a Docket</h3>
                      <p className="text-gray-700">
                        Browse dockets by searching for a proceeding number (e.g., "23-320") to find the FCC documents you want to analyze.
                      </p>
                    </div>

                    <div className="border-l-4 border-orange-500 pl-6 py-2">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Step 4: Start Asking Questions</h3>
                      <p className="text-gray-700">
                        Choose your chat mode and start asking questions. CommsGenie will retrieve relevant information 
                        and provide detailed, citation-backed answers.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Understanding Your Dashboard */}
                <section id="understanding-dashboard" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Your Dashboard</h2>
                  <p className="text-gray-700 mb-4">
                    Your CommsGenie dashboard is your central hub for accessing documents and tracking your research activity.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Sidebar Navigation</h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li>• <strong>Home:</strong> Access homepage and getting started resources</li>
                        <li>• <strong>Chat:</strong> Open your conversation interface</li>
                        <li>• <strong>Resources:</strong> Documentation, releases, feedback, and support</li>
                        <li>• <strong>Account:</strong> Manage settings, billing, and team workspaces</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Main Content Area</h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li>• <strong>Chat Interface:</strong> Ask questions and view AI responses</li>
                        <li>• <strong>Document Viewer:</strong> Read and analyze FCC documents</li>
<li>• <strong>Usage Stats:</strong> Track your token usage and plan limits</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Core Features */}
                <section id="core-features" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Features</h2>

                  {/* Proceedings */}
                  <div id="proceedings" className="mb-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="w-6 h-6 mr-2 text-blue-600" />
                      Proceedings - FCC Docket Analysis
                    </h3>
                    <p className="text-gray-700 mb-4">
                      <strong>What it does:</strong> Access and analyze FCC proceedings, including NPRMs (Notices of Proposed Rulemaking), 
                      comment filings, and related documents.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-3">Key capabilities:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• View all documents filed in a proceeding</li>
                        <li>• Generate AI-powered summaries of comment filings</li>
                        <li>• Track stakeholder positions and arguments</li>
                        <li>• Search across all comments in a docket</li>
                        <li>• Export summaries for regulatory reports</li>
                      </ul>
                    </div>
                    <p className="text-gray-700 mb-4">
                      <strong>Best for:</strong> Reviewing public comments, understanding stakeholder positions, preparing regulatory responses
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">How to use:</h4>
                      <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                        <li>Search for a proceeding by docket number (e.g., "WC Docket No. 23-320")</li>
                        <li>Browse the list of filed comments and documents</li>
                        <li>Click "Generate Summary" on any comment to get an AI analysis</li>
                        <li>Use Chat mode to ask questions about the entire proceeding</li>
                      </ol>
                    </div>
                  </div>

                  {/* Chat Modes */}
                  <div id="chat-modes" className="mb-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                      <MessageCircle className="w-6 h-6 mr-2 text-green-600" />
                      Chat Modes
                    </h3>
                    <p className="text-gray-700 mb-4">
                      CommsGenie offers three specialized chat modes, each optimized for different research needs:
                    </p>
                    <div className="overflow-x-auto mb-6">
                      <table className="min-w-full border border-gray-300 rounded-lg">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Mode</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Best For</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">How It Works</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          <tr className="border-b">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Standard Chat</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Quick questions, clarifications</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Fast responses using retrieval from selected documents</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Speed</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Rapid lookups, simple facts</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Streamlined retrieval for instant answers</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h4 className="font-semibold text-green-900 mb-3">Choosing Your Chat Mode:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>Use Standard Chat when:</strong> You need balanced speed and depth for typical research questions</li>
                        <li><strong>Use Speed when:</strong> You need quick factual lookups or simple clarifications</li>
                      </ul>
                    </div>
                  </div>

                  {/* Workspaces */}
                  <div id="workspaces" className="mb-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Users className="w-6 h-6 mr-2 text-indigo-600" />
                      Workspaces - Team Collaboration
                    </h3>
                    <p className="text-gray-700 mb-4">
                      <strong>What it does:</strong> Collaborate with colleagues by sharing chat histories and research findings.
                    </p>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-indigo-900 mb-3">Key capabilities:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Create team workspaces for law firms or practice groups</li>
                        <li>• Share research and chat histories with team members</li>
                        <li>• Control access permissions (view, edit, admin)</li>
                        <li>• Centralize regulatory research for your organization</li>
                        <li>• Track team usage and activity</li>
                      </ul>
                    </div>
                    <p className="text-gray-700 mb-4">
                      <strong>Best for:</strong> Law firms, regulatory teams, collaborative research projects
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Managing Workspaces:</h4>
                      <ol className="space-y-3 text-gray-700 list-decimal list-inside">
                        <li><strong>Create a Workspace:</strong> Go to Account → Workspaces → New Workspace</li>
                        <li><strong>Invite Team Members:</strong> Add colleagues by email with appropriate permission levels</li>
                        <li><strong>Collaborate:</strong> Team members can view chat histories and build on each other's research</li>
                      </ol>
                    </div>
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Permission Levels:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>Admin:</strong> Full control - manage members and billing</li>
                        <li><strong>Editor:</strong> Manage workspace content and invite members</li>
                        <li><strong>Viewer:</strong> Read-only access to workspace content</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Advanced Features */}
                <section id="advanced-features" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Features</h2>

                  {/* Search & Discovery */}
                  <div id="search-discovery" className="mb-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Search className="w-6 h-6 mr-2 text-blue-600" />
                      Document Search & Discovery
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Quickly find FCC documents using CommsGenie's search tools:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>Search by docket number:</strong> "23-320", "WC Docket No. 17-108"</li>
                      <li>• <strong>Search by keywords:</strong> "broadband", "universal service", "spectrum auction"</li>
                      <li>• <strong>Filter by date range:</strong> Find documents from specific time periods</li>
                      <li>• <strong>Filter by document type:</strong> Orders, NPRMs, Public Notices, Comments</li>
                    </ul>
                  </div>

                  {/* Comment Summary */}
                  <div id="comment-summary" className="mb-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="w-6 h-6 mr-2 text-green-600" />
                      Comment Summary Generation
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Automatically generate AI-powered summaries of public comments:
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-green-900 mb-3">Features:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>Publication-ready quality:</strong> Summaries suitable for regulatory reports</li>
                        <li><strong>Structured format:</strong> Key arguments, supporting evidence, recommendations</li>
                        <li><strong>Stakeholder identification:</strong> Automatically identifies filer type and interests</li>
                        <li><strong>Citation extraction:</strong> Pulls out key citations and legal references</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">How to generate:</h4>
                      <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                        <li>Navigate to a Proceeding</li>
                        <li>Find a comment filing</li>
                        <li>Click "Generate Summary"</li>
                        <li>Review and export the summary</li>
                      </ol>
                    </div>
                  </div>

                  {/* Export */}
                  <div id="export" className="mb-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Download className="w-6 h-6 mr-2 text-indigo-600" />
                      Export & Integration
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Export your research and summaries:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>PDF Export:</strong> Download complete summaries with citations</li>
                      <li>• <strong>Copy to Clipboard:</strong> Quickly copy text for reports and memos</li>
                      <li>• <strong>Share Links:</strong> Generate shareable links to specific analyses</li>
                    </ul>
                  </div>
                </section>

                {/* Account & Billing */}
                <section id="account-billing" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Account & Billing</h2>
                  
                  <div id="usage-tiers" className="mb-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Understanding Usage Tiers</h3>
                    <p className="text-gray-700 mb-4">
                      CommsGenie uses token-based pricing for AI messages:
                    </p>
                    <div className="overflow-x-auto mb-6">
                      <table className="min-w-full border border-gray-300 rounded-lg">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Plan</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Monthly Cost</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">AI Messages Included</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Best For</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          <tr className="border-b">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Free</td>
                            <td className="px-4 py-3 text-sm text-gray-700">$0</td>
                            <td className="px-4 py-3 text-sm text-gray-700">5 messages</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Trial and exploration</td>
                          </tr>
                          <tr className="border-b">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Professional</td>
                            <td className="px-4 py-3 text-sm text-gray-700">$49/month</td>
                            <td className="px-4 py-3 text-sm text-gray-700">~500 messages</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Individual attorneys</td>
                          </tr>
                          <tr className="border-b">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Team</td>
                            <td className="px-4 py-3 text-sm text-gray-700">$199/month</td>
                            <td className="px-4 py-3 text-sm text-gray-700">~2,500 messages</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Small practice groups</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">Enterprise</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Custom</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Unlimited</td>
                            <td className="px-4 py-3 text-sm text-gray-700">Large firms and organizations</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-3">What counts as usage:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Each chat message (question + answer)</li>
                        <li>• Each document summary generated</li>
                        <li>• Multi-document analyses use more tokens than single-document chats</li>
                      </ul>
                    </div>
                    <p className="text-gray-700">
                      <strong>View your usage:</strong> Check your dashboard for real-time usage tracking and remaining message credits.
                    </p>
                  </div>

                  <div id="managing-subscription" className="mb-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Managing Your Subscription</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>Upgrade/Downgrade:</strong> Change plans anytime from Account Settings</li>
                      <li>• <strong>Billing History:</strong> View invoices and payment history</li>
                      <li>• <strong>Usage Alerts:</strong> Get notified when approaching your monthly limit</li>
                      <li>• <strong>Auto-renewal:</strong> Subscriptions renew automatically unless cancelled</li>
                    </ul>
                  </div>
                </section>

                {/* Best Practices */}
                <section id="best-practices" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Best Practices</h2>

                  <div id="effective-questions" className="mb-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Writing Effective Questions</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-green-900 mb-3">For Standard Chat:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• <strong>Be specific:</strong> "What did AT&T argue about the proposed timeline?" vs. "What did they say?"</li>
                        <li>• <strong>Reference document sections:</strong> "In the NPRM, what questions does the FCC ask about affordability?"</li>
                        <li>• <strong>Ask follow-ups:</strong> Build on previous answers to go deeper</li>
                      </ul>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                      <h4 className="font-semibold text-purple-900 mb-3">For multi-document analysis:</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• <strong>Ask comparative questions:</strong> "Compare the Commission's reasoning in the 2015 and 2017 Open Internet Orders"</li>
                        <li>• <strong>Request temporal analysis:</strong> "How has industry position on this issue evolved since 2020?"</li>
                        <li>• <strong>Seek synthesis:</strong> "What are the main areas of consensus and disagreement across all comments?"</li>
                      </ul>
                    </div>
                  </div>

                  <div id="research-workflows" className="mb-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Regulatory Research Workflows</h3>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-3">Comment Analysis Workflow:</h4>
                      <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                        <li>Navigate to the proceeding</li>
                        <li>Generate summaries for key comments (major industry players, advocacy groups)</li>
                        <li>Select the NPRM and major comments for analysis</li>
                        <li>Compare stakeholder positions across documents</li>
                        <li>Export summaries for your regulatory response</li>
                      </ol>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-4">
                      <h4 className="font-semibold text-purple-900 mb-3">Temporal Analysis Workflow:</h4>
                      <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                        <li>Select orders on the same topic across years</li>
                        <li>Ask: "How has the FCC's policy evolved?"</li>
                        <li>Identify key inflection points and reasoning changes</li>
                        <li>Generate a timeline of policy development</li>
                      </ol>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h4 className="font-semibold text-green-900 mb-3">Due Diligence Workflow:</h4>
                      <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                        <li>Search for all proceedings related to a client's issue</li>
                        <li>Select relevant orders and NPRMs for analysis</li>
                        <li>Use chat to quickly find precedents and applicable rules</li>
                        <li>Export key findings for client memo</li>
                      </ol>
                    </div>
                  </div>

                  <div id="tips-tricks" className="mb-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4">Tips & Tricks</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>Use quote search:</strong> Ask Claude to "find where the Commission discusses [specific term]"</li>
                      <li>• <strong>Request citations:</strong> Add "with citations" to get specific paragraph references</li>
                      <li>• <strong>Iterate your questions:</strong> Start broad, then narrow based on responses</li>
                      <li>• <strong>Leverage Speed mode:</strong> For quick fact-checks during calls or meetings</li>
                      <li>• <strong>Save complex queries:</strong> Bookmark important multi-document analyses</li>
                    </ul>
                  </div>
                </section>

                {/* FAQ */}
                <section id="faq" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Q: How current is the FCC data?</h4>
                      <p className="text-gray-700">
                        A: CommsGenie syncs with the FCC's ECFS database daily. Documents typically appear within 24 hours of filing.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Q: Can I upload my own documents?</h4>
                      <p className="text-gray-700">
                        A: Yes! Upload PDFs, Word documents, and other files to analyze alongside FCC documents.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Q: How accurate are the summaries?</h4>
                      <p className="text-gray-700">
                        A: Summaries are generated using GPT-4 and achieve publication-quality accuracy. However, always review 
                        AI-generated content before using in official filings.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Q: Is my research confidential?</h4>
                      <p className="text-gray-700">
                        A: Yes. Your chats and workspaces are private. Only invited team members can access workspace content.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Q: What if I run out of messages?</h4>
                      <p className="text-gray-700">
                        A: You can upgrade your plan anytime or purchase additional message credits. Free users can upgrade to 
                        continue chatting immediately.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Q: Can I cancel anytime?</h4>
                      <p className="text-gray-700">
                        A: Yes. Cancel from Account Settings. You'll retain access through the end of your billing period.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Support - Getting Help */}
                <section id="getting-help" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Getting Help</h2>
                  <p className="text-gray-700 mb-6">
                    Need assistance? We're here to help:
                  </p>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span><strong>Email Support:</strong> support@commsgenie.com</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span><strong>Feature Requests:</strong> Use the feedback button in-app</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span><strong>Bug Reports:</strong> support@commsgenie.com with screenshots</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span><strong>Onboarding:</strong> Schedule a demo for team training</span>
                    </li>
                  </ul>
                </section>
              </article>
            </div>
          </div>

        {/* Table of Contents - Right sidebar */}
        <div className="hidden xl:block w-64 py-8 px-6">
          <TableOfContents
            items={tocItems}
            activeId={activeSection}
            onItemClick={setActiveSection}
          />
        </div>
      </div>
    </div>
  );
};

